<script setup lang="ts">
import { getWorkOrderMinersExportCSV } from '@/services/rc/work-orders.service'
import { useOIDCStore } from '@/stores/oidc.store'
import { ElMessage } from 'element-plus'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'
import { Icon } from '@iconify/vue'
import BulkMinersDialog from '@/components/rc/work-order-dashboard/BulkMinersDialog.vue'
import MinersConsolidationDialog from '@/components/rc/miner-repairs/consolidation/MinersConsolidationDialog.vue'
import MinersTable from '@/components/rc/tables/MinersTable.vue'
import { useWorkOrder } from '@/composables/rc/work-order'
import RlErrorAlert from '@/components/common/RlErrorAlert.vue'
import { downloadFile } from '@/util'

const props = defineProps<{ number: string }>()

const oidcStore = useOIDCStore()
const { isRcAppManagerUser } = storeToRefs(oidcStore)

const isBulkMinersDialogOpen = ref<boolean>(false)
const isLoadingExport = ref<boolean>(false)

const showConsolidationDialog = ref(false)

const handleImport = () => {
  isBulkMinersDialogOpen.value = true
}

const { workOrderDashboardSummaryQuery, isManageableByCurrentUser, isOutsourced } = useWorkOrder(props.number)
const { data, isError, isPending, error } = workOrderDashboardSummaryQuery()

/**
 * Handle work order miners export click.
 *
 * @param workOrderNumber work order number for which to export miners.
 */
const handleExport = async (workOrderNumber: string) => {
  isLoadingExport.value = true
  try {
    const result = await getWorkOrderMinersExportCSV(workOrderNumber)
    downloadFile(result, `${workOrderNumber}_miners_export.csv`)
  } catch (err: any) {
    ElMessage.error(err.message)
  } finally {
    isLoadingExport.value = false
  }
}
</script>

<template>
  <MinersConsolidationDialog
    v-if="data"
    v-model="showConsolidationDialog"
    :work-order="number"
    :customer-id="data.work_order_information.customer.id.toString()"
  />
  <RlErrorAlert v-if="isError && !isPending" :error="error" />
  <MinersTable :work-order-number="number" :editable="isManageableByCurrentUser">
    <template #controls>
      <el-button v-if="isManageableByCurrentUser" @click="showConsolidationDialog = true">
        <span class="flex items-center gap-2">
          <icon icon="material-symbols:compare-arrows" :height="20" />
          Consolidation
        </span>
      </el-button>
      <el-button :loading="isLoadingExport" @click="handleExport(number)">Export</el-button>
      <el-button v-if="isRcAppManagerUser && !isOutsourced" @click="handleImport()">Import</el-button>
    </template>
  </MinersTable>
  <BulkMinersDialog
    v-model="isBulkMinersDialogOpen"
    :work-order-number="number"
    @close="isBulkMinersDialogOpen = false"
  />
</template>

<style scoped lang="scss">
.el-button {
  margin: 0;
}
</style>
